import i18next from 'i18next';
import CreatableSelect from 'react-select/creatable';
import ReactQuill from 'react-quill';
import MultiFileUploader from '../MultiFileUploader/MultiFileUploader';
import useLeadsStore from '../../store/LeadsStore';
import SpinnerButton from '../SpinnerButton/SpinnerButton';
import * as Yup from 'yup';
import ValidationError from '../ValidationError/ValidationError';
import useModalStore from '../../store/ModalStore';
import { Markets, ReactSelectOption } from '../../types';
import { useTranslation } from 'react-i18next';
import { Alert, Col, Row, Form, Button } from 'react-bootstrap';
import { OPPORTUNITY_SUPPORTED_FILE_TYPES } from '../../utils/constants';
import { emailIsValid, getDerivedRecordTypeId, randomString, removeDuplicates, strToFloat } from '../../utils';
import { MultiValue, SingleValue, ActionMeta } from 'react-select';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ClienteEnum, LeadStatusEnum, LeadsWizardActiveStepEnum, OpportunityStageEnum, RoleEnum } from '../../api/models/Enums';
import { useApiClient } from '../../hooks/useApiClient';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { Formik } from 'formik';
import { GTMsendEvent } from '../../utils/trackingGA4';
import { useErrorToast } from "../../hooks/useErrorToast";
import { toast } from 'react-toastify';
import { VehicleUpdateRequestDTO } from '../../api';
import { Storage } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from "@tanstack/react-query";
import _ from 'lodash';
import { isMarket } from 'src/utils/markets';
import { useAuth } from 'src/hooks/useAuth';

const SendEmail = () => {
  const { t } = useTranslation();
  const { setLeadWizardData, setWizardActiveStep, activeLead, cleanLeadWizardData, leadWizardData, cleanActiveLead, isConvertingLead, setIsConvertingLead } = useLeadsStore();
  const { showError } = useErrorToast();
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { closeModal } = useModalStore();
  const { apiClient, handleApiResponse } = useApiClient();
  const { userRole } = useAuth();

  const emailTemplateIt = `
Spett.le Cliente,
<br>
con la presente le invio il preventivo per la soluzione di noleggio da lei richiesta.
<br>
In attesa di un suo gentile riscontro resto a disposizione per ogni dubbio.
<br>
Cordiali saluti
<br>
  `;

  const emailTemplateEs = `
Estimado cliente,
<br>
por la presente le envío el presupuesto para la solución de alquiler que ha solicitado.
<br>
Esperando su amable respuesta, quedo a su disposición para cualquier consulta.
<br>
Un cordial saludo
<br>
  `;
  const updateWizardState = useCallback((values: any) => {
    setLeadWizardData({
      ...leadWizardData,
      sendEmailData: {
        ...leadWizardData.sendEmailData,
        ...values,
      },
    });
  }, [leadWizardData, setLeadWizardData])

  const uploadFilesToBucket = async (files) => {
    let filesToUploadList: { filename: string, key: string, file: File, type: string }[] = []

    const uploadContent = {
      //'sendFiles': { data: files },
      'uploadFiles': { data: files }
    }

    for (const type in uploadContent) {
      const fileArray: { data: File[], type: 'sendFiles' | 'uploadFiles' } = uploadContent[type]

      if (fileArray.data.length) {
        let uploadOps: any[] = []

        fileArray.data.forEach(file => {
          const ext = file.name.split('.').pop()
          const key = `opportunity_uploads/${randomString(10)}.${ext}`
          filesToUploadList.push({ type, file, filename: file.name, key })
        })

        uploadOps = filesToUploadList.map(obj => {
          return Storage.put(obj.key, obj.file, {
            contentType: obj.file.type,
            bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
          });
        })

        try {
          await Promise.all(uploadOps)
        } catch (e) {
          console.error(e);
          toast.error(i18next.t('error_loading_file'))
          throw e;
        }
      }
    }

    return filesToUploadList.map(el => {
      return { ...el, key: `public/${el.key}` }
    })
  }

  const sendLabel = useCallback((values: any) => {
    return values?.enabled ? t('save_and_send') : t('save');
  }, [t]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        enabled: Yup.boolean(),
        to: Yup.object().when('enabled', {
          is: true,
          then: (schema) =>
            schema
              .shape({
                value: Yup.string().required(t('required_field')),
                label: Yup.string().required(t('required_field')),
              })
              .required(t('required_field'))
              .test('valid-email', t('invalid_email'), (value) => {
                return value ? emailIsValid(value.value) : false;
              }),
          otherwise: (schema) => schema.nullable(),
        }),

        cc: Yup.array().of(
          Yup.object().shape({
            value: Yup.string().test('valid-email', t('invalid_email'), (value) =>
              value ? emailIsValid(value) : true
            ),
          })
        ),
        message: Yup.string().when('enabled', {
          is: true,
          then: (schema) => schema.required(t('required_field')),
          otherwise: (schema) => schema.notRequired(),
        }),
        files: Yup.array().notRequired(),
        // files: Yup.array().min(1, t('upload_file_required')).required(t('required_field')),
      }),
    [t]
  );

  const initialValues = useRef({
    enabled: leadWizardData?.sendEmailData?.enabled || false,
    to: leadWizardData?.account?.Email__c
      ? { value: leadWizardData.account.Email__c, label: leadWizardData.account.Email__c }
      : leadWizardData?.sendEmailData?.to || null,
    cc: leadWizardData?.sendEmailData?.cc || [],
    message: leadWizardData?.sendEmailData?.message || (isMarket(Markets.es) ? emailTemplateEs : emailTemplateIt),
    files: leadWizardData?.sendEmailData?.files || [],
  }
  );

	const getCommissionsField = () => {
		if ([RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.SALES_INTERNAL].includes(userRole)) {
			return 'commissions';
		}

		if ([RoleEnum.AREA_MANAGER, RoleEnum.SALES_DEALER].includes(userRole)) {
			return 'provvigioniPassive';
		}

		return 'commissions';
	};

  const handleFormSubmit = async (values) => {
    try {
      setIsConvertingLead(true)
      const data = Object.assign({}, leadWizardData, { sendEmailData: { ...values } })
      setLoading(true);
      let filesToUploadList = await uploadFilesToBucket(values.files)
      const isAziendaClient = data?.account?.Cliente__c === ClienteEnum.Azienda;

      // CREATE SF ACCOUNT
      // need to map Cliente__c to RecordTypeId needed for create account api
      let createAccountPayload: any = {
        Codice_Fiscale__c: data.account.Codice_Fiscale__c,
        Partita_IVA__c: data.account.Partita_IVA__c,
        RecordTypeId: getDerivedRecordTypeId(data.account.Cliente__c!),
        Email__c: data.account.Email__c,
        Phone: data.account.Phone,
        BillingCity:  data.account.BillingCity,
			  BillingStreet:  data.account.BillingStreet,
        Flotta_Potenziale_PC__c: data.account.Flotta_Potenziale_PC__c ?? undefined,
        Flotta_Potenziale_LCV__c: data.account.Flotta_Potenziale_LCV__c ?? undefined,
        Flotta_Totale_LCV__c: data.account.Flotta_Totale_LCV__c ?? undefined,
        Flotta_Totale_PC__c: data.account.Flotta_Totale_PC__c ?? undefined,
        reference_partner: data.account.reference_partner,
        ...(isAziendaClient ? { Name: `${data.account.FirstName} ${data.account.LastName} ` } : {
          FirstName: data.account.FirstName,
          LastName: data.account.LastName
        }),
        ID_Lead__c: activeLead?.Id,
      }

      let newAccount

      if (!data.account.Id) {
        try {
          newAccount = await handleApiResponse(apiClient.salesforceAccountController.createAccount(createAccountPayload))
          GTMsendEvent({
            event: 'complete_new_account',
            category: 'account',
            action: 'complete',
            label: 'new_account'
          })
          toast.success(i18next.t('account_created_success'))
        } catch (error) {
          console.error("Error editing lead:", error);
          showError(error, i18next.t('error_editing_account'));
          throw new Error(i18next.t('error_editing_account'))
        }
      }

      const isMasterOrBackoffice = [RoleEnum.MASTER, RoleEnum.BACKOFFICE].includes(userRole);
      const isSegnalazione = data.opportunity.Canale__c?.value?.toLowerCase() === 'segnalatore';
      const commissionsField = getCommissionsField();

      const provvPayload = isMasterOrBackoffice
        ? {
            commissions: data.opportunity.commissions
              ? strToFloat(data.opportunity.commissions.toString())
              : 0,
            provvigioniPassive: data.opportunity.provvigioniPassive
              ? strToFloat(data.opportunity.provvigioniPassive.toString())
              : undefined,
          }
        : { [commissionsField]: strToFloat(data.opportunity.commissions.toString() || '') };

      // CREATE SF OPPORTUNITY
      // TODO dare una entitty a sta roba
      let createOpportunityPayload: any = {
        accountId: data.account.Id ? data.account.Id : newAccount?.id,
        ...provvPayload,
        provvigioniSegnalatore: isSegnalazione && data.opportunity.provvigioniSegnalatore
          ? strToFloat(data.opportunity.provvigioniSegnalatore.toString())
          : undefined,
        segnalatore: isSegnalazione ? (data.opportunity.segnalatore || undefined) : undefined,
        Ragione_Sociale_Segnalatore__c: isSegnalazione ? (data.opportunity.ragioneSocialeSegnalatore || undefined) : undefined,
        channel: data.opportunity.Canale__c?.value,
        Tipologia__c: data.opportunity.Tipologia__c?.value,
        Noleggiatore__c: data.opportunity.Tipologia__c?.value,
        name: `HMP-${data.account.Name}-${data.opportunity.name}`,
        dealerName: data.opportunity.Noleggiatore__c?.value,
        emailSending: null,
        signedAttachments: null,
        note: data.opportunity.notes || activeLead?.Messaggio__c,
        stageName: OpportunityStageEnum.proposal,
        vehicle: {
          vehicleId: data.vehicle?.fullVehicleData?.id,
          customVehicle: data.vehicle?.customVehicle,
        },
        driveItEasy: data.opportunity.driveItEasy,
        driverEmail: data.opportunity.driverEmail,
        nomePartner: _.isObject(data.opportunity?.nomePartner)
          ? data.opportunity?.nomePartner?.value
          : data.opportunity?.nomePartner,
        isConvertedFromLead__c: true,
        LeadId__c: activeLead.Id,
        LeadSource: activeLead.LeadSource,
        Canale_del_lead__c: activeLead.Canale_del_lead__c,
        Offerta_Edison__c: data.opportunity.Offerta_Edison__c?.value,
        salesUser: data.opportunity.salesUser,
        referenceUserId: data.opportunity.referenceUserId,
        backupUsers: data.opportunity.backupUsers,
        Data_Creazione_Lead__c: activeLead?.Data_Creazione_Lead__c || activeLead?.CreatedDate,
      }

      if (isMarket(Markets.es)) {
        createOpportunityPayload = _.omit(createOpportunityPayload, ['Offerta_Edison__c'])
      }

      const attachments = filesToUploadList.map(item => {
        return {
          "filename": item.filename,
          "key": item.key
        }
      })

      const to = typeof data?.sendEmailData?.to === "string"
        ? data?.sendEmailData?.to
        // @ts-ignore
        : data?.sendEmailData?.to?.value;

      if (data?.sendEmailData?.enabled) {
        createOpportunityPayload.emailSending = {
          "to": to,
          "cc": data?.sendEmailData?.cc?.map(el => el.value),
          "body": data?.sendEmailData?.message,
          "uploadedAttachments": attachments
        }
      }

      createOpportunityPayload.signedAttachments = attachments
      let newOpportunity
      try {
        newOpportunity = await handleApiResponse(apiClient.salesforceOpportunityController.createOpportunity(createOpportunityPayload, { opportunity_source: 'lead_conversion' }))
        toast.success(i18next.t('opportunity_created_successfully'))
      } catch (error) {
        console.error("Error creating opportunity:", error);
        showError(error, i18next.t('error_creating_opportunity'));
        throw new Error(i18next.t('error_creating_opportunity'))
      }

      // CONVERT LEAD
      try {
        // const payload = {
        //   ConvertedAccountId: data.account.Id ? data.account.Id : newAccount?.id,
        //   ConvertedDate: new Date().toISOString(),
        //   ConvertedOpportunityId: newOpportunity?.id,
        //   IsConverted: true,
        // }

        await handleApiResponse(apiClient.leadsControllerService.convertLead(activeLead.Id, LeadStatusEnum.QUALIFIED))
        toast.success(i18next.t('lead_converted_successfully'));
      } catch (error) {
        console.error("Error converting lead into opportunity:", error);
        showError(error, i18next.t('error_converting_lead_into_opportunity'));
        throw new Error(i18next.t('error_converting_lead_into_opportunity'));
      }

      // UPDATE opportunityId in stato_corrente of VEHICLE HMP
      // HMP VEHICLE
      if (data?.vehicle) {
        const fullVehicleData = data.vehicle?.fullVehicleData;

        if (fullVehicleData && !_.isEmpty(fullVehicleData)) {
          const vehicleData = fullVehicleData;
          const updatedStatoCorrente = { opportunityId: newOpportunity.id };

          const updateVehiclePayload: VehicleUpdateRequestDTO = {
            stato_corrente: updatedStatoCorrente,
            codice_costruttore: vehicleData?.dettagli_veicolo?.codice_costruttore ?? '',
            codice_colore_esterno: vehicleData?.dettagli_veicolo?.codice_colore_esterno ?? '',
            codice_colore_interno: vehicleData?.dettagli_veicolo?.codice_colore_interno ?? '',
            dealer: vehicleData?.dettagli_veicolo?.dealer ?? '',
            nr_ordine: vehicleData?.dettagli_veicolo?.nr_ordine ?? '',
            id_bo: vehicleData?.dettagli_veicolo?.id_bo,
            city: vehicleData?.dettagli_veicolo?.city,
            colore_esterno: vehicleData?.dettagli_veicolo?.colore_esterno,
            colore_interno: vehicleData?.dettagli_veicolo?.colore_interno,
            data_disp: vehicleData?.dettagli_veicolo?.data_disp,
            data_immatricolazione: vehicleData?.dettagli_veicolo?.data_immatricolazione,
            data_telaio: vehicleData?.dettagli_veicolo?.data_telaio,
            giorno_disp: vehicleData?.dettagli_veicolo?.giorno_disp,
            note: vehicleData?.dettagli_veicolo?.note,
            optionals: vehicleData?.optionals,
            promo: vehicleData?.dettagli_veicolo?.promo,
            promo_note: vehicleData?.dettagli_veicolo?.promo_note,
            settimana_prod: vehicleData?.dettagli_veicolo?.settimana_prod,
            targa: vehicleData?.dettagli_veicolo?.targa,
            telaio: vehicleData?.dettagli_veicolo?.telaio,
            document: vehicleData?.dettagli_veicolo?.document,
            prezzo_listino: vehicleData?.dati_veicolo?.prezzo_listino,
            in_evidenza: vehicleData?.dettagli_veicolo?.in_evidenza,
            lista_noleggiatori_quotatore: vehicleData?.lista_noleggiatori_quotatore,
            extra_sconto_perc: vehicleData?.dati_veicolo?.extra_sconto_perc,
				    limited_visibility: vehicleData?.dettagli_veicolo?.limited_visibility || [],
          };

          try {
            await handleApiResponse(
              apiClient.vehicleController.putVehicles(vehicleData?.id as string, updateVehiclePayload)
            );
          } catch (err) {
            console.error(err);
            toast.error(i18next.t('error_updating_vehicle'));
          }
        }
      }

      if (newOpportunity.id) {
        navigate(`/opportunities/${newOpportunity.id}`);
      }

      queryClient.invalidateQueries({ queryKey: ["leads_list"] });
      cleanLeadWizardData()
      cleanActiveLead()
      closeModal()
      // prossimo wizard apre da qui
      setWizardActiveStep(LeadsWizardActiveStepEnum.ACCOUNT)
      setLoading(false)
    } catch (error) {
      throw error
    } finally {
      setIsConvertingLead(false);
    }
  }

  const handleGoBack = (values) => {
    if (!values.enabled) {
      values = Object.assign(values, {
        cc: [],
        message: null,
        to: null,
      })
    }
    setLeadWizardData({
      sendEmailData: {
        ...leadWizardData.sendEmailData,
        ...values
      },
    });
    setWizardActiveStep(LeadsWizardActiveStepEnum.VEHICLE)
  }

  return (
    <div>
      {!leadWizardData.account ? (
        <Alert>{t('account_selection_required_to_proceed')}</Alert>
      ) : (
        <Formik
          initialValues={initialValues.current}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            updateWizardState(values)
            handleFormSubmit(values)
          }}
          enableReinitialize={true}
        >
          {({ errors, values, handleChange, setFieldValue, touched, handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              {userRole === RoleEnum.SALES_DEALER ? null
                : (<Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {values.enabled ? i18next.t('send_email') : i18next.t('no_send_email')}
                    </Form.Label>
                    <Form.Check
                      name="enabled"
                      id="sendEmail"
                      type="switch"
                      onChange={(e) => {
                        setFieldValue('enabled', e.target.checked);
                      }}
                      checked={values.enabled}
                      className="form-check-switch"
                    />
                  </Form.Group>
                </Col>)}

              {values.enabled && (
                <div className="mt-4">
                  <div className="my-4">
                    <hr />
                  </div>
                  <Row>
                    {/* TO recipient */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{i18next.t('to')}</Form.Label>
                        <CreatableSelect
                          isClearable
                          components={{ DropdownIndicator: null }}
                          placeholder="A"
                          options={values.to ? [typeof values.to === 'string' ? { value: values.to, label: values.to } : values.to] : []}
                          noOptionsMessage={() => i18next.t('add_customer_email')}
                          value={typeof values.to === 'string' ? { value: values.to, label: values.to } : values.to}
                          formatCreateLabel={(i) => (emailIsValid(i) ? `Invia a ${i}` : `${i}`)}
                          onChange={(newValue: SingleValue<ReactSelectOption>, actionMeta: ActionMeta<ReactSelectOption>) => {
                            const opt = typeof newValue === 'string' ? { value: newValue, label: newValue } : newValue;
                            if (!opt || emailIsValid(opt.value)) {
                              setFieldValue('to', opt);

                            }
                          }}
                        />
                        <ValidationError {...{ errors, touched, name: 'to' }} />
                      </Form.Group>
                    </Col>

                    {/* C.c recipient */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{i18next.t('cc')}</Form.Label>
                        <CreatableSelect
                          isMulti
                          components={{ DropdownIndicator: null }}
                          placeholder="C.c."
                          noOptionsMessage={() => i18next.t('add_cc_recipients')}
                          value={values.cc}
                          formatCreateLabel={(i) => (emailIsValid(i) ? `Aggiungi ${i}` : `${i}`)}
                          onChange={(newValue: MultiValue<ReactSelectOption>) => {
                            const filtered: ReactSelectOption[] = removeDuplicates(
                              newValue.filter((el) => emailIsValid(el.value))
                            );
                            setFieldValue('cc', filtered);

                          }}
                        />
                        <ValidationError {...{ errors, touched, name: 'cc' }} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col xs={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>{i18next.t('message')}</Form.Label>
                        <ReactQuill
                          theme="snow"
                          placeholder={i18next.t('write_email')}
                          value={values.message}
                          onChange={(message) => {
                            setFieldValue('message', message)
                          }}
                          onBlur={() => {
                            setLeadWizardData({
                              sendEmailData: {
                                ...leadWizardData.sendEmailData,
                                ...values,
                                message: values.message
                              },
                            });
                          }}
                        />
                        <ValidationError {...{ errors, touched, name: 'message' }} />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              <div className="mt-4">
                <MultiFileUploader
                  files={values.files}
                  maxSize={25 * 1024 * 1024}
                  setFiles={(newFiles) =>
                    setFieldValue('files', newFiles)
                  }
                  types={OPPORTUNITY_SUPPORTED_FILE_TYPES}
                />
                <ValidationError {...{ errors, touched, name: 'files' }} />
              </div>

              <Row>
                <Col className="d-flex justify-content-between align-items-center">
                  <Button
                    onClick={() => handleGoBack(values)}
                    style={{ marginTop: '10px', marginRight: '10px' }}
                  >
                    <FaArrowLeftLong className="me-2" />
                    {t('vehicle')}
                  </Button>
                  <Button type="submit" style={{ marginTop: '10px' }} disabled={loading || isConvertingLead}>
                    <SpinnerButton label={sendLabel(values)} busy={loading || isConvertingLead} />
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default SendEmail;
